import { useEffect } from 'react';

import { selectIsAccountEligible } from 'modules/referralProgram/store/selectors';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAppSelector } from 'store/useAppSelector';
import { useApplyReferralCode } from 'modules/referralProgram/hooks/useApplyReferralCode';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { usePersonalAccountStatus } from 'modules/personalAccount/hooks/usePersonalAccountStatus';
import { useSavedReferralCode } from 'modules/referralProgram/hooks/useSavedReferralCode';
import { useUser } from 'modules/user/hooks/useUser';
import { useValidateReferralCode } from 'modules/referralProgram/hooks/useValidateReferralCode';

import { useReferrer } from './useReferrer';

export interface IUseInitReferralFlowWithSavedCodeProps {
  handleIneligibleAccountDialogOpen: () => void;
  handleSuccessDialogOpen: () => void;
  handleSwitchAccountDialogOpen: () => void;
  isCoverLoaded: boolean;
}

export const useInitReferralFlowWithSavedCode = ({
  handleIneligibleAccountDialogOpen,
  handleSuccessDialogOpen,
  handleSwitchAccountDialogOpen,
  isCoverLoaded,
}: IUseInitReferralFlowWithSavedCodeProps) => {
  const { isLoggedIn } = useAuth();
  const { isUserInitialized } = useUser();

  const { isPersonalAccount } = useAccount();

  const {
    isPersonalAccountFreemium,
    isPersonalAccountStatusLoaded,
    userEndpointToken,
  } = usePersonalAccountStatus({ skipFetching: !isUserInitialized });

  const isAccountEligible = useAppSelector(state =>
    selectIsAccountEligible(state, { userEndpointToken: userEndpointToken! }),
  );

  const { handleRemoveSavedReferralCode, savedReferralCode } =
    useSavedReferralCode();

  const { handleApplyReferralCode, isApplied, isApplying } =
    useApplyReferralCode({
      hasSuccessNotification: false,
      onError: handleRemoveSavedReferralCode,
      onSuccess: handleSuccessDialogOpen,
      referralCode: savedReferralCode,
      // needs to display Success dialog correctly
      shouldRemoveSavedData: false,
    });

  const { validateReferralCode } = useValidateReferralCode();

  const {
    hasReferrer,
    isLoaded: isReferrerLoaded,
    isLoading: isReferrerLoading,
  } = useReferrer();

  useEffect(() => {
    const error = validateReferralCode(savedReferralCode);

    const shouldContinueReferralFlow =
      !error &&
      isLoggedIn &&
      isCoverLoaded &&
      isPersonalAccountStatusLoaded &&
      !hasReferrer &&
      isReferrerLoaded &&
      !isApplying &&
      !isReferrerLoading &&
      !isApplied;

    if (shouldContinueReferralFlow) {
      if (isAccountEligible) {
        handleApplyReferralCode();
      } else if (!isPersonalAccount && isPersonalAccountFreemium) {
        handleSwitchAccountDialogOpen();
      } else {
        handleIneligibleAccountDialogOpen();
      }
    }
    // Only data should be tracked, not callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasReferrer,
    isAccountEligible,
    isApplied,
    isApplying,
    isCoverLoaded,
    isLoggedIn,
    isPersonalAccount,
    isPersonalAccountFreemium,
    isPersonalAccountStatusLoaded,
    isReferrerLoaded,
    isReferrerLoading,
    savedReferralCode,
  ]);
};
